import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { formStyles } from './shared-styles.js';
import { AuthService, authService as defaultAuthService } from '../services/auth-service.js';

/**
 * "Forgot password" form. Because the server intentionally responds identically whether or not the
 * email exists (to avoid account enumeration), this form always shows the same confirmation message
 * on submit.
 */
@customElement('gc-forgot-password-form')
export class GcForgotPasswordForm extends LitElement {
  static override styles = formStyles;

  /** Injectable for testing; defaults to the shared AuthService. */
  authService: AuthService = defaultAuthService;

  @state()
  private loading = false;

  @state()
  private submitted = false;

  override render() {
    if (this.submitted) {
      return html`<p class="success" role="status">
        If an account exists for that email, a password reset link is on its way.
      </p>`;
    }
    return html`
      <form @submit=${this.onSubmit}>
        <label>
          Email
          <input type="email" name="email" autocomplete="username" required />
        </label>
        <button type="submit" ?disabled=${this.loading}>
          ${this.loading ? 'Sending…' : 'Send reset link'}
        </button>
      </form>
    `;
  }

  private async onSubmit(event: Event) {
    event.preventDefault();
    if (this.loading) {
      return;
    }
    const form = event.target as HTMLFormElement;
    const email = String(new FormData(form).get('email') ?? '');

    this.loading = true;
    try {
      await this.authService.forgotPassword(email);
    } catch {
      // Deliberately ignored: the UX is identical regardless of outcome.
    } finally {
      this.loading = false;
      this.submitted = true;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gc-forgot-password-form': GcForgotPasswordForm;
  }
}
